import { useQuery } from '@tanstack/react-query';
import dataService from 'services/dataService';

import { apiUrlGenerator } from '../use-api';

import type {
  IPatientEngagementReportRow,
  TPatientEngagementReportFilter,
} from './types';

const url = 'reports/reports/patient-engagement-status-report';
export const PATIENT_ENGAGEMENT_REPORT_PER_PAGE = 10;

interface IPatientEngagementReportResponse {
  items: IPatientEngagementReportRow[];
  count: number;
}

/**
 * The report table. Filtering and pagination are server-side; `page` is
 * 1-indexed to match the backend's pagination contract.
 */
export const useGetPatientEngagementReport = (
  filter: TPatientEngagementReportFilter,
) => {
  const queryFilter = {
    clinicGuid: filter.clinicGuid,
    parentClinicGuid: filter.parentClinicGuid,
    doctorGuid: filter.doctorGuid,
    status: filter.status,
    patientGuid: filter.patientGuid,
    engagementAttempts: filter.engagementAttempts,
    page: filter.page,
    items: PATIENT_ENGAGEMENT_REPORT_PER_PAGE,
  };

  const { data, isLoading, isFetching } = useQuery<
    IPatientEngagementReportResponse | undefined,
    string | undefined
  >({
    queryKey: ['getPatientEngagementReport', queryFilter],
    queryFn: async () => {
      const { data: response, error } =
        await dataService.getList<IPatientEngagementReportResponse>(
          apiUrlGenerator(url, queryFilter),
        );
      if (error) return Promise.reject(error);
      return response;
    },
    keepPreviousData: true,
  });

  return {
    data: data?.items ?? [],
    total: data?.count ?? 0,
    isLoading: isLoading || isFetching,
  };
};
